import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {SelectComboboxList, SelectItem, SelectVirtualList} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {teamGroupToLabel} from '@/features/projects/utils'
import {useGetInfiniteTeams} from '@/features/treatment-requests/teams/queries/useGetInfiniteTeams'
import {TeamGroup} from '@/features/treatment-requests/teams/types'
import {StyledGroupText} from '@/features/users/components/input-select-user/style'
import {useTranslation} from '@/translations/i18n'
import {useMemo} from 'react'

export const InputSelectTeamMembersList = ({
    disabled,
    isQueryEnabled,
    disabledOptions,
    treatmentRequestId,
    search,
    projectId
}: {
    disabled: boolean
    isQueryEnabled: boolean
    disabledOptions: string[]
    treatmentRequestId: string
    search?: string
    projectId?: string
}) => {
    const {t} = useTranslation()
    const {data, isLoading, isError, hasNextPage, fetchNextPage} = useGetInfiniteTeams({
        urlParams: {treatmentRequestId},
        params: {responseType: 'small', limit: 20, page: 1, search, projectId},
        options: {enabled: !!(treatmentRequestId && isQueryEnabled && !disabled)}
    })

    // Set of disabled options
    const disabledSet = useMemo(() => new Set(disabledOptions), [disabledOptions])

    // Item content renderer
    const itemContent = useMemo(() => {
        return (
            itemIndex: number,
            item: {id: string; fullName: string; group: TeamGroup; specializationName?: string}
        ) => (
            <SelectItem hasItemCheckbox key={itemIndex} value={item.id} disabled={disabledSet.has(item.id)}>
                {item.fullName}
                {item.group && (
                    <StyledGroupText
                        palette="neutral"
                        render="span"
                        shade="500"
                        size="textXs"
                        $isSelected={disabledSet.has(item.id)}
                    >
                        {` `}
                        {item.specializationName ? `${item.specializationName} - ` : ''}
                        {t(teamGroupToLabel[item.group])}
                    </StyledGroupText>
                )}
            </SelectItem>
        )
    }, [disabledSet])

    const teamMembers = useMemo(() => data?.pages.flatMap(page => page.data) ?? [], [data])

    return (
        <SelectComboboxList>
            {isLoading ? (
                Array.from({length: 6}, (_, index) => <Skeleton key={index} height={32} />)
            ) : isError ? (
                <InputHelpText error={t('errors:default')} />
            ) : teamMembers.length > 0 ? (
                <SelectVirtualList
                    data={teamMembers.map(member => ({
                        id: member.user.id,
                        fullName: member.user.fullName,
                        group: member.group,
                        specializationName: member.user.specializationName
                    }))}
                    totalCount={teamMembers.length}
                    overscan={100}
                    endReached={() => hasNextPage && fetchNextPage()}
                    itemContent={itemContent}
                />
            ) : (
                <InputHelpText helpText={t('commons:no_results')} />
            )}
        </SelectComboboxList>
    )
}
